import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FilterType, RegionProps, SidoList } from '../FilterData';
import RegionService from '../../../service/Region';
import {
  ApplyButton,
  Bottom,
  ButtonBox,
  ResetButton,
} from './OptionModal.styles';

interface Props {
  onClose: () => void;
  filters: FilterType;
  setFilters: (filters: FilterType) => void;
  onlyRegion?: boolean;
}

function RegionOption({ onClose, filters, setFilters, onlyRegion }: Props) {
  const [address, setAddress] = useState('');
  const [sido, setSido] = useState<RegionProps | null>(null);
  const [sigungu, setSigungu] = useState<RegionProps | null>(null);
  const [emd, setEmd] = useState<RegionProps | null>(null);
  const [sigunguList, setSigunguList] = useState<RegionProps[]>([]);
  const [emdList, setEmdList] = useState<RegionProps[]>([]);

  useEffect(() => {
    const initSido =
      SidoList.find((item) => item.region === filters.region[0]?.sido) || null;
    setSido(initSido);

    if (initSido) {
      getRegion(initSido.code).then((sigunguRes) => {
        setSigunguList(sigunguRes);
        const initSigungu =
          sigunguRes.find(
            (item) => item.region === filters.region[0]?.sigungu,
          ) || null;
        setSigungu(initSigungu);

        if (initSigungu) {
          getRegion(initSigungu.code).then((emdRes) => {
            setEmdList(emdRes);
            const filteredEmds = emdRes.filter(
              (item) => item.region !== '전체',
            );
            const initEmd =
              filteredEmds.find(
                (item) => item.region === filters.region[0]?.emd,
              ) || null;
            setEmd(initEmd);
          });
        }
      });
    }
  }, []);

  const getRegion = async (code: string) => {
    try {
      const res = await RegionService.list(code);
      return res.data as RegionProps[];
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  useEffect(() => {
    if (sido !== null) {
      getRegion(sido.code).then((res) => setSigunguList(res));
    }
  }, [sido]);

  useEffect(() => {
    if (sigungu !== null) {
      getRegion(sigungu.code).then((res) => setEmdList(res));
    }
  }, [sigungu]);

  const makeAllOption = (regionName: string): RegionProps => ({
    code: 'all',
    region: regionName + ' 전체',
  });

  const sidoOptions = [{ code: 'all', region: '전국' }, ...SidoList];
  const sigunguOptions =
    sido && sido.code !== 'all'
      ? [makeAllOption(sido.region)].concat(sigunguList)
      : [];

  const filterEmdList = emdList.filter((item) => item.region !== '전체');
  const emdOptions =
    sigungu && sigungu.code !== 'all'
      ? [makeAllOption(sigungu.region)].concat(filterEmdList)
      : [];

  const handleReset = () => {
    setAddress('');
    setSido(null);
    setSigungu(null);
    setEmd(null);
    setSigunguList([]);
    setEmdList([]);
  };

  const handleSubmit = () => {
    setFilters({
      ...filters,
      address: address,
      region:
        sido || sigungu || emd
          ? [
              {
                sido: sido?.region ?? '',
                sigungu: sigungu?.code === 'all' ? '' : (sigungu?.region ?? ''),
                emd: emd?.code === 'all' ? '' : (emd?.region ?? ''),
              },
            ]
          : [],
    });
    setAddress('');
    onClose();
  };

  const handleAddressSubmit = () => {
    setFilters({
      ...filters,
      region: [],
      address: address,
    });
    setSido(null);
    setSigungu(null);
    setEmd(null);
    setSigunguList([]);
    setEmdList([]);
    onClose();
  };

  return (
    <Wrapper>
      {!onlyRegion && (
        <Box>
          상세주소로 검색
          <InputWrapper>
            <Input
              placeholder="주소 검색"
              value={address}
              onChange={(e) => {
                if (sido) {
                  setSido(null);
                  setSigungu(null);
                  setEmd(null);
                }
                setAddress(e.target.value);
              }}
            />
            <img
              src="/assets/images/icon/search-gray.svg"
              alt="search"
              width={24}
              height={24}
              onClick={handleAddressSubmit}
            />
          </InputWrapper>
        </Box>
      )}
      <RegionBox>
        <Box>
          {!onlyRegion && '지역으로 검색'}
          <InputWrapper>
            {sido === null ? (
              <RegionText>도/시 선택</RegionText>
            ) : (
              <SelectedRegionText>{sido.region}</SelectedRegionText>
            )}
            <img
              src="/assets/images/arrow/arrow_right.svg"
              alt=""
              style={{ width: '24px', height: '24px' }}
            />
            {sigungu === null ? (
              <RegionText>시/군/구 선택</RegionText>
            ) : (
              <SelectedRegionText>{sigungu.region}</SelectedRegionText>
            )}
            <img
              src="/assets/images/arrow/arrow_right.svg"
              alt=""
              style={{ width: '24px', height: '24px' }}
            />
            {emd === null ? (
              <RegionText>읍/면/동 선택</RegionText>
            ) : (
              <SelectedRegionText>{emd.region}</SelectedRegionText>
            )}
          </InputWrapper>
        </Box>
        <RegionSelector>
          <SidoColumn>
            {sidoOptions.map((item) => (
              <SidoItem
                key={item.code}
                active={item === sido}
                onClick={() => {
                  if (address) {
                    setAddress('');
                  }
                  if (item.code === 'all') {
                    setSido(item);
                    setSigungu(null);
                    setEmd(null);
                    setSigunguList([]);
                    setEmdList([]);
                  } else {
                    setSido(item);
                  }
                }}
              >
                {item.region}
              </SidoItem>
            ))}
          </SidoColumn>
          <Column>
            {sigunguOptions.map((item) => (
              <Item
                key={item.code}
                active={item.code === sigungu?.code}
                onClick={() => {
                  if (item.code === 'all') {
                    setSigungu(item);
                    setEmd(null);
                    setEmdList([]);
                  } else {
                    setSigungu(item);
                  }
                }}
              >
                {item.region}
              </Item>
            ))}
          </Column>
          <Column>
            {emdOptions.map((item) => (
              <Item
                key={item.code}
                active={item.code === emd?.code}
                onClick={() => setEmd(item)}
              >
                {item.region}
              </Item>
            ))}
          </Column>
        </RegionSelector>
      </RegionBox>
      <Bottom>
        <ButtonBox>
          <ResetButton onClick={handleReset}>
            <img
              src="/assets/images/icon/reset.svg"
              alt=""
              width={24}
              height={24}
            />
            <span>초기화</span>
          </ResetButton>
          <ApplyButton onClick={handleSubmit}>적용</ApplyButton>
        </ButtonBox>
      </Bottom>
    </Wrapper>
  );
}

export default RegionOption;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Box = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #e1e2e4;
  padding: 24px 16px;
  gap: 16px;

  font-weight: 500;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
`;

const RegionBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const InputWrapper = styled.div`
  min-width: 0;
  width: 100%;
  padding: 8px 16px;
  border-radius: 8px;
  background-color: #70737c0d;
  display: flex;
  flex-direction: row;
`;

const Input = styled.input`
  min-width: 0;
  flex: 1;
  border: none;
  background-color: transparent;

  font-weight: 500;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;

  &::placeholder {
    color: #616161;
    font-weight: 500;
    font-size: 16px;
    line-height: 145%;
    letter-spacing: -2%;
  }

  -webkit-appearance: none;
  appearance: none;

  &::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }

  &:focus {
    outline: none;
    border-color: rgba(23, 30, 38, 0.3);
  }
`;

const RegionText = styled.span`
  font-family: Spoqa Han Sans Neo;

  font-size: 16px;
  color: #37383c9c;
  flex: 1;
  white-space: nowrap;
  text-align: center;
`;
const SelectedRegionText = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  font-size: 16px;
  flex: 1;
  white-space: nowrap;
`;

const RegionSelector = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1px;
  /* height: 100px; */
  flex: 1;
  overflow: hidden;
`;

const Column = styled.div`
  background-color: #fff;
  flex: 1;
  overflow-y: auto;
  border-right: 1px solid #e1e2e4;
`;

const SidoColumn = styled.div`
  width: 80px;
  background-color: #f5f5f5;
  overflow-y: auto;
  border-right: 1px solid #e1e2e4;
`;

const Item = styled.div<{ active: boolean }>`
  padding: 12px;
  cursor: pointer;
  color: ${({ active }) => (active ? '#008AFF' : '#222')};
  background-color: ${({ active }) => (active ? '#EAF2FE' : '#fff')};
  font-family: Pretendard;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  text-align: center;
`;

const SidoItem = styled(Item)`
  color: ${({ active }) => (active ? '#222222' : '#37383C9C')};
  background-color: ${({ active }) => (active ? '#FFFFFF' : '#F5F5F5')};
`;
